/**
 * §11.1 Trust Center Hero — "Trust, security & transparency."
 *
 * Left: eyebrow + heading + subtitle + CTAs + status pill
 * Right: layered shield visualisation — a concentric "trust stack"
 *        showing Identity → Network → Application → Data → Governance
 *        with the Kalki Ai Cloud shield at the core.
 *
 * We deliberately DO NOT display any specific certification badges
 * here (SOC 2, ISO, etc.) — those live further down under an honest
 * status label to avoid overclaiming.
 */

import React from 'react'
import {
  ShieldCheck, Sparkles, Lock, Network, KeyRound, Database, ScrollText,
  Building2, ArrowRight,
} from 'lucide-react'
import {
  Container, Eyebrow, GradientButton, GhostButton,
} from '@/components/landing/design-system'

// Concentric "trust stack" — outer to inner.
const STACK = [
  { label: 'Governance', iconKey: 'ScrollText', tone: '#f59e0b', r: 170 },
  { label: 'Data',       iconKey: 'Database',   tone: '#a855f7', r: 138 },
  { label: 'Application',iconKey: 'Lock',       tone: '#3b82f6', r: 106 },
  { label: 'Network',    iconKey: 'Network',    tone: '#22d3ee', r:  76 },
  { label: 'Identity',   iconKey: 'KeyRound',   tone: '#22c55e', r:  48 },
]
const ICONS = { ScrollText, Database, Lock, Network, KeyRound, ShieldCheck, Building2, Sparkles }

export default function Hero() {
  const cx = 280, cy = 210
  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(34,197,94,0.10), transparent 70%), radial-gradient(60% 45% at 50% 20%, rgba(255,193,7,0.10), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />

      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Eyebrow tone="green">Trust Center</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Trust, security &{' '}
              <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">transparency.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              How Kalki Ai Cloud protects the businesses that run on us — security
              posture, privacy commitments, data protection, platform availability,
              compliance roadmap and legal policies, published honestly.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="#security" size="lg">
                <ShieldCheck className="h-4 w-4" /> Explore security
              </GradientButton>
              <GhostButton href="/contact?topic=security" size="lg" trailingIcon>
                Contact security team
              </GhostButton>
            </div>

            {/* Live status pill — mocked green */}
            <div className="mt-2 inline-flex items-center gap-2 self-start rounded-full border border-emerald-400/25 bg-emerald-400/[0.06] px-3 py-1.5 text-[11.5px] font-semibold text-emerald-300">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span><span className="text-white">All systems operational</span> · 99.98% uptime · trailing 90 days</span>
            </div>

            <div className="mt-1 flex flex-wrap gap-2">
              {['GDPR · DPA available', 'ISO 27001 · on roadmap', 'AES-256 at rest · TLS 1.2+', 'Sub-processor list · documented'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <Sparkles className="h-3 w-3 text-brand-gold" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — concentric trust stack */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 50% 50%, rgba(34,197,94,0.10), transparent 70%)' }} />

              <svg viewBox="0 0 560 420" className="relative w-full h-auto">
                <defs>
                  <radialGradient id="tc-center" cx="50%" cy="50%" r="50%">
                    <stop offset="0%"   stopColor="#22c55e" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* Concentric rings + a single small icon marker on each */}
                {STACK.map((s, idx) => {
                  const Icon = ICONS[s.iconKey] || ShieldCheck
                  // Cascade the icon markers along the top-right arc so
                  // they never collide — spread evenly across -80°..+80°.
                  const deg = -80 + idx * 40
                  const rad = (deg * Math.PI) / 180
                  const mx  = cx + s.r * Math.cos(rad)
                  const my  = cy + s.r * Math.sin(rad)
                  return (
                    <g key={s.label}>
                      <circle cx={cx} cy={cy} r={s.r} fill="none"
                        stroke={`${s.tone}55`} strokeWidth="1.1" strokeDasharray={idx === 0 ? '2 6' : undefined} />
                      {/* small icon marker on the ring */}
                      <g transform={`translate(${mx} ${my})`}>
                        <circle r="12" fill="rgba(10,10,12,0.95)" stroke={`${s.tone}99`} strokeWidth="1.2" />
                        <foreignObject x="-7" y="-7" width="14" height="14">
                          <div className="flex items-center justify-center w-full h-full" style={{ color: s.tone }}>
                            <Icon strokeWidth={1.9} style={{ width: 10, height: 10 }} />
                          </div>
                        </foreignObject>
                      </g>
                    </g>
                  )
                })}

                {/* Core shield */}
                <g transform={`translate(${cx} ${cy})`}>
                  <circle r="36" fill="url(#tc-center)" />
                  <circle r="26" fill="rgba(10,10,12,0.95)" stroke="rgba(34,197,94,0.55)" strokeWidth="1.6" />
                  <foreignObject x="-14" y="-14" width="28" height="28">
                    <div className="flex items-center justify-center w-full h-full text-emerald-300">
                      <ShieldCheck strokeWidth={1.9} style={{ width: 22, height: 22 }} />
                    </div>
                  </foreignObject>
                </g>
              </svg>

              {/* Legend — one row per ring, tone-tinted */}
              <div className="relative mt-3 grid grid-cols-5 gap-1.5">
                {STACK.map((s) => {
                  const Icon = ICONS[s.iconKey] || ShieldCheck
                  return (
                    <div key={s.label} className="flex flex-col items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-1.5 py-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border" style={{ borderColor: `${s.tone}55`, background: `${s.tone}14`, color: s.tone }}>
                        <Icon strokeWidth={1.9} style={{ width: 12, height: 12 }} />
                      </span>
                      <div className="text-[10.5px] font-semibold text-zinc-200 leading-none text-center">{s.label}</div>
                    </div>
                  )
                })}
              </div>

              <div className="relative mt-2 flex items-center justify-between rounded-md border border-emerald-400/20 bg-emerald-400/[0.05] px-3 py-2">
                <div className="flex items-center gap-2 text-[10.5px] text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span><span className="text-white font-semibold">Layered controls</span>, published SLAs, honest posture</span>
                </div>
                <div className="flex items-center gap-1 text-[10.5px] text-emerald-400 font-mono">
                  updated monthly <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
